import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import api from '../../utils/api';
import { 
  Camera, QrCode, MapPin, CheckCircle2, AlertTriangle, 
  ArrowLeft, Clock, LogIn, LogOut, X, Loader2, ClipboardList
} from 'lucide-react';
import { formatTime12h, formatTimeRange, formatDateTime12h, formatDateMMDDYYYY } from '../../utils/timeFormat';
import AddToCalendarButton from '../../components/AddToCalendarButton';
import QRScannerModal from '../../components/QRScannerModal';

// Req-4 (2026-09-14) — Some backend gates (e.g. checkin_too_early) return a
// STRUCTURED detail object ({ code, message, minutes_until_open, ... }) rather
// than a plain string. Rendering that object directly showed "[object Object]"
// and made talent think the app was broken. This normalizes any error shape
// into a clear, human-readable string, preferring the server's own message.
const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    if (detail.message) return detail.message;
    if (typeof detail.minutes_until_open === 'number') {
      return `Check-in isn't open yet — about ${detail.minutes_until_open} minute(s) to go.`;
    }
  }
  return fallback;
};

const TalentCheckIn = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();   // Iter-291 — read ?qr=…&action=…
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const locationRef = useRef(null);
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  // Iter-411i — GPS diagnostic state. `locationErrorCode` is the
  // GeolocationPositionError code (1=permission_denied, 2=unavailable,
  // 3=timeout) so we can display a specific hint AND ship the reason
  // to the backend audit log. `locationAttempts` gates the auto-retry
  // loop. `usingFallback` reflects whether the talent has explicitly
  // clicked "Check in without GPS".
  const [locationErrorCode, setLocationErrorCode] = useState(null);
  const [locationAttempts, setLocationAttempts] = useState(0);
  const [usingFallback, setUsingFallback] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [qrInput, setQrInput] = useState(searchParams.get('qr') || '');  // Iter-291 — pre-fill from /talent/qr-scan
  const [showQrInput, setShowQrInput] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);             // Iter-291 — camera QR scanner modal
  const [checkinRecord, setCheckinRecord] = useState(null);
  // V412 — Client Lead Capture config for this booking's client.
  // Populated only after check-in so the "Capture Lead" button is
  // gated behind the same rule as the ops team surfacing (locked
  // out before check-in and after clock-out).
  const [leadCapture, setLeadCapture] = useState(null);

  // Fetch booking details
  const fetchBooking = useCallback(async () => {
    try {
      const response = await api.get(`/talent/bookings/${bookingId}`);
      setBooking(response.data);
      
      // Check if already checked in
      if (response.data.clock_in_time && !response.data.clock_out_time) {
        setCheckinRecord({ checked_in: true });
      } else if (response.data.clock_out_time) {
        setCheckinRecord({ checked_in: true, checked_out: true });
      }
    } catch (error) {
      toast.error('Failed to load booking details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  // V412 — Fetch client's lead-capture URL once we know the booking
  // is loaded. We fetch UNCONDITIONALLY so the button gate can be
  // driven purely off `isCheckedIn && !isCheckedOut && leadCapture.enabled`
  // — no extra request on click.
  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/talent/bookings/${bookingId}/lead-capture`);
        if (!cancelled) setLeadCapture(res.data);
      } catch {
        if (!cancelled) setLeadCapture({ enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  // Iter-411i — GPS acquisition strategy.
  //
  // What Samantha's incident taught us:
  //   • The old code was ONE attempt at high-accuracy with a 10s timeout,
  //     one-shot "Unable to get your location" error, and no fallback.
  //     If Chrome/Safari couldn't fix a GPS point in 10s, or the OS
  //     reported the sensor as unavailable, the button stayed disabled
  //     and the talent could not check in even while physically onsite.
  //
  // What we do now:
  //   1. First attempt — high accuracy, 15s timeout (most modern phones
  //      indoors need > 10s for GPS+Wi-Fi fusion).
  //   2. On timeout OR position-unavailable → retry with `enableHighAccuracy=false`
  //      and a 20s timeout so we accept coarse Wi-Fi/cell-tower position.
  //   3. On permission-denied → surface a clear, actionable hint AND
  //      immediately expose the "Check in without GPS" fallback button.
  //   4. Every failure captures the GeolocationPositionError code so the
  //      backend can log permission_denied vs timeout vs unavailable
  //      (audit-critical for wage-hour disputes).
  //
  // The check-in button is NEVER disabled by GPS state — talent onsite
  // can always fall back with a reason and admin review.
  const codeToLabel = (code) => {
    switch (code) {
      case 1: return 'permission_denied';
      case 2: return 'position_unavailable';
      case 3: return 'timeout';
      default: return 'unknown';
    }
  };
  const codeToHint = (code) => {
    switch (code) {
      case 1:
        return "Location access was denied. Open your browser's site "
             + "settings for iEvntür, toggle location on, then refresh. "
             + "If you're onsite, tap 'Check in without GPS' below.";
      case 2:
        return "Your device says GPS is unavailable right now. Try "
             + "walking outside if you're indoors, or tap 'Check in "
             + "without GPS' — a shift lead can verify you.";
      case 3:
        return "GPS timed out. iEvntür will retry automatically. If it "
             + "keeps failing, tap 'Check in without GPS' to continue.";
      default:
        return "We couldn't get your GPS location. You can still check "
             + "in without GPS — an admin will review it.";
    }
  };

  const acquireLocation = useCallback((highAccuracy = true, timeoutMs = 15000) => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLocationErrorCode(2);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        locationRef.current = loc;
        setLocation(loc);
        setLocationError(null);
        setLocationErrorCode(null);
      },
      (error) => {
        console.warn('[checkin] geolocation attempt failed', {
          code: error.code, message: error.message, highAccuracy, timeoutMs,
        });
        setLocationErrorCode(error.code);
        setLocationError(codeToHint(error.code));
        setLocationAttempts((n) => n + 1);
        // Auto-retry ONE time with lower accuracy + longer timeout,
        // but only for timeout / position-unavailable (not permission
        // denied — retrying that would just prompt again in a loop).
        if (highAccuracy && (error.code === 3 || error.code === 2)) {
          setTimeout(() => acquireLocation(false, 20000), 500);
        }
      },
      { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: 30000 },
    );
  }, []);

  // Get GPS location on mount (falls back automatically if the first
  // high-accuracy attempt times out or reports position-unavailable).
  //
  // GPS WATCHDOG (P0 — onsite lockout fix): navigator.geolocation can
  // hang forever with NO callback (mobile Safari with "Ask" permission,
  // indoor venues, backgrounded tab). In that case neither `location`
  // nor `locationErrorCode` is ever set — so the "Check in without GPS"
  // fallback button never appeared and the talent was fully stuck. The
  // watchdog forces a timeout state after 12s so the escape hatch is
  // ALWAYS reachable, even when the OS never fires success or error.
  useEffect(() => {
    acquireLocation(true, 15000);
    const watchdog = setTimeout(() => {
      if (!locationRef.current) {
        setLocationErrorCode((c) => (c == null ? 3 : c));
        setLocationError((e) => e || codeToHint(3));
      }
    }, 12000);
    return () => clearTimeout(watchdog);
  }, [acquireLocation]);

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
    } catch (error) {
      toast.error('Unable to access camera. Please grant camera permissions.');
      console.error('Camera error:', error);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  // Upload selfie to get URL
  const uploadSelfie = async (imageData) => {
    try {
      const response = await api.post('/cloudinary/upload', {
        file: imageData,
        folder: 'checkin_selfies'
      });
      return response.data.secure_url || response.data.url;
    } catch (error) {
      console.error('Failed to upload selfie:', error);
      return null;
    }
  };

  // Check-in handler
  const handleCheckIn = async (opts = {}) => {
    // Iter-411i — `opts.viaFallback` is set by the "Check in without GPS"
    // button below. When true we skip the require_gps_checkin gate,
    // pass the geolocation-error code to the backend, and let the
    // admin review queue pick up the record.
    const viaFallback = !!opts.viaFallback;

    if (!location && !viaFallback && booking?.shift?.require_gps_checkin) {
      // Onsite-lockout fix: never leave the talent with no path forward.
      // Reveal the "Check in without GPS" fallback immediately (in case
      // GPS is still resolving or silently hung) and tell them exactly
      // where to tap.
      setLocationErrorCode((c) => (c == null ? 3 : c));
      toast.error(
        'GPS isn\'t ready yet. Give it a few seconds, or tap '
        + '"Check in without GPS (I\'m onsite)" just below to continue now.',
        { duration: 6000 },
      );
      return;
    }

    setCheckingIn(true);
    try {
      let selfieUrl = null;
      if (capturedImage) {
        selfieUrl = await uploadSelfie(capturedImage);
      }

      const payload = {
        booking_id: bookingId,
        latitude: viaFallback ? null : location?.latitude,
        longitude: viaFallback ? null : location?.longitude,
        selfie_url: selfieUrl,
        qr_token: qrInput || null,
        // Iter-411i — new fields the backend uses to distinguish and
        // audit the failure mode.
        gps_fallback: viaFallback,
        gps_fallback_reason: viaFallback ? codeToLabel(locationErrorCode) : null,
        // Iter-411t — when using the onsite fallback with no selfie,
        // flag selfie_failed=true so the backend does not hard-reject
        // on selfie-required shifts. The record still goes to admin
        // review, protecting the talent's Reliability Score.
        selfie_failed: viaFallback && !selfieUrl,
        device_info: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
      };

      const response = await api.post('/attendance/checkin', payload);

      if (response.data.gps_fallback_used) {
        toast.warning(
          response.data.message
            || 'Checked in — GPS unavailable, flagged for admin review',
          { duration: 8000 },
        );
      } else if (response.data.gps_warning) {
        toast.warning(response.data.gps_warning, { duration: 6000 });
      } else {
        toast.success('Checked in successfully!');
      }

      setCheckinRecord({ checked_in: true, ...response.data });
      setUsingFallback(false);
      await fetchBooking();
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to check in');
      toast.error(message, { duration: 7000 });
    } finally {
      setCheckingIn(false);
    }
  };

  // Check-out handler
  const handleCheckOut = async (opts = {}) => {
    // Iter-411i — check-out mirrors check-in: talent who successfully
    // checked in with GPS but LOST GPS by the end of their shift must
    // still be able to clock out. `viaFallback=true` posts
    // gps_fallback=true and the fallback flows into the same admin
    // review queue.
    const viaFallback = !!opts.viaFallback;
    setCheckingOut(true);
    try {
      let selfieUrl = null;
      if (capturedImage) {
        selfieUrl = await uploadSelfie(capturedImage);
      }

      const payload = {
        booking_id: bookingId,
        latitude: viaFallback ? null : location?.latitude,
        longitude: viaFallback ? null : location?.longitude,
        selfie_url: selfieUrl,
        gps_fallback: viaFallback,
        gps_fallback_reason: viaFallback ? codeToLabel(locationErrorCode) : null,
        // Iter-411t — mirror the check-in fix: onsite fallback without
        // a selfie must not be blocked on selfie-required shifts.
        selfie_failed: viaFallback && !selfieUrl,
        device_info: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
      };

      const res = await api.post('/attendance/checkout', payload);
      if (res.data?.gps_fallback_used) {
        toast.warning(
          res.data.message
            || 'Checked out — GPS unavailable, flagged for admin review',
          { duration: 8000 },
        );
      } else {
        toast.success('Checked out successfully!');
      }
      setCheckinRecord((prev) => ({ ...prev, checked_out: true }));
      await fetchBooking();
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to check out');
      toast.error(message, { duration: 7000 });
    } finally {
      setCheckingOut(false);
    }
  };

  // Iter-411k — event-code verification. Talent invokes this from the
  // fallback UI when both GPS and QR are unavailable and the onsite
  // lead has read them the 6-char code.
  const [eventCode, setEventCode] = useState('');
  const [showEventCode, setShowEventCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const verifyEventCode = async () => {
    if (!eventCode || eventCode.trim().length < 4) {
      toast.error('Enter the 6-character event code from your onsite lead.');
      return;
    }
    setVerifyingCode(true);
    try {
      const res = await api.post('/attendance/verify-event-code', {
        booking_id: bookingId,
        event_code: eventCode.trim().toUpperCase(),
      });
      toast.success(res.data?.message || 'Checked in via event code — verified onsite.');
      setCheckinRecord({ checked_in: true, ...res.data });
      setShowEventCode(false);
      await fetchBooking();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Event code did not match'));
    } finally {
      setVerifyingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-lg mx-auto text-center py-12">
          <h2 className="text-xl font-semibold text-slate-700">Booking not found</h2>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const shift = booking.shift || {};
  const isCheckedIn = checkinRecord?.checked_in || booking.clock_in_time;
  const isCheckedOut = checkinRecord?.checked_out || booking.clock_out_time;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Event Check-In</h1>
            <p className="text-sm text-gray-400">{shift.venue_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {/* Event Info Card */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base">{shift.venue_name}</CardTitle>
                <CardDescription>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-4 h-4" />
                    {formatDateMMDDYYYY(shift.shift_date)} • {formatTimeRange(shift.start_time, shift.end_time)}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <MapPin className="w-4 h-4" />
                    {shift.city}, {shift.state}
                  </div>
                </CardDescription>
              </div>
              {/* Iter-156f — Add to Calendar on the booking detail page */}
              <AddToCalendarButton bookingId={bookingId} />
            </div>
          </CardHeader>
        </Card>

        {/* iter417/iter418 — Onsite / Arrival Details (read-only for talent) */}
        {(shift.booth_number || shift.reporting_location || shift.entrance_instructions ||
          shift.onsite_directions || shift.parking_instructions || shift.info_desk_location ||
          shift.arrival_instructions || (Array.isArray(shift.floor_plan_urls) && shift.floor_plan_urls.length > 0)) && (
          <Card className="border-blue-200 bg-blue-50/40" data-testid="talent-onsite-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" /> Where to go
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1.5 text-sm">
              {shift.booth_number && (
                <div data-testid="talent-onsite-booth"><span className="font-medium">Booth / table #:</span> {shift.booth_number}</div>
              )}
              {shift.reporting_location && (
                <div data-testid="talent-onsite-report"><span className="font-medium">Report to:</span> {shift.reporting_location}</div>
              )}
              {shift.info_desk_location && (
                <div data-testid="talent-onsite-infodesk"><span className="font-medium">Registration / info desk:</span> {shift.info_desk_location}</div>
              )}
              {shift.entrance_instructions && (
                <div data-testid="talent-onsite-entrance"><span className="font-medium">Entrance:</span> <span className="whitespace-pre-wrap">{shift.entrance_instructions}</span></div>
              )}
              {shift.onsite_directions && (
                <div data-testid="talent-onsite-directions"><span className="font-medium">Onsite directions:</span> <span className="whitespace-pre-wrap">{shift.onsite_directions}</span></div>
              )}
              {shift.parking_instructions && (
                <div data-testid="talent-onsite-parking"><span className="font-medium">Parking:</span> <span className="whitespace-pre-wrap">{shift.parking_instructions}</span></div>
              )}
              {shift.arrival_instructions && (
                <div data-testid="talent-onsite-arrival"><span className="font-medium">Arrival:</span> <span className="whitespace-pre-wrap">{shift.arrival_instructions}</span></div>
              )}
              {Array.isArray(shift.floor_plan_urls) && shift.floor_plan_urls.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className="font-medium">Floor plans / maps:</span>
                  {shift.floor_plan_urls.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer"
                       className="text-blue-600 hover:underline" data-testid={`talent-onsite-map-${i}`}>
                      {p.name || p.url}
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className={isCheckedOut ? 'border-blue-200 bg-blue-50/50' : isCheckedIn ? 'border-green-200 bg-green-50/50' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Current Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {isCheckedOut ? (
                    <Badge className="bg-blue-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Checked Out
                    </Badge>
                  ) : isCheckedIn ? (
                    <Badge className="bg-green-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Checked In
                    </Badge>
                  ) : (
                    <Badge variant="outline">
                      <Clock className="w-3 h-3 mr-1" /> Not Checked In
                    </Badge>
                  )}
                </div>
              </div>
              {booking.clock_in_time && (
                <div className="text-right text-sm">
                  <p className="text-gray-500">Check-in time</p>
                  <p className="font-medium text-green-600">
                    {formatDateTime12h(booking.clock_in_time)}
                  </p>
                  {booking.clock_out_time && (
                    <>
                      <p className="text-gray-500 mt-1">Check-out time</p>
                      <p className="font-medium text-blue-600">
                        {formatDateTime12h(booking.clock_out_time)}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* GPS Status */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className={`w-5 h-5 ${location ? 'text-green-500' : locationErrorCode ? 'text-amber-500' : 'text-gray-500'}`} />
                <div>
                  <p className="font-medium text-sm">GPS Location</p>
                  <p className="text-xs text-gray-400" data-testid="gps-status-text">
                    {location
                      ? `Accuracy: ${location.accuracy.toFixed(0)}m`
                      : locationError || 'Acquiring location...'}
                  </p>
                </div>
              </div>
              {location ? (
                <Badge variant="outline" className="text-green-600 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Ready
                </Badge>
              ) : locationErrorCode ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-cyan-600 h-8"
                  onClick={() => acquireLocation(true, 15000)}
                  data-testid="gps-retry-btn"
                >
                  <Loader2 className={`w-3.5 h-3.5 mr-1 ${locationAttempts > 0 && !locationErrorCode ? 'animate-spin' : ''}`} />
                  Retry GPS
                </Button>
              ) : (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Camera / Selfie Section */}
        {!isCheckedOut && (
          <Card>
            <CardContent className="pt-4">
              {showCamera ? (
                <div className="space-y-3">
                  <div className="relative aspect-video bg-gray-50 rounded-lg overflow-hidden">
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      playsInline 
                      muted 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={capturePhoto} className="flex-1">
                      <Camera className="w-4 h-4 mr-2" /> Capture
                    </Button>
                    <Button variant="outline" onClick={stopCamera}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : capturedImage ? (
                <div className="space-y-3">
                  <div className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden">
                    <img src={capturedImage} alt="Selfie" className="w-full h-full object-cover" />
                    <Badge className="absolute top-2 right-2 bg-green-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Captured
                    </Badge>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => { setCapturedImage(null); startCamera(); }}
                  >
                    <Camera className="w-4 h-4 mr-2" /> Retake Photo
                  </Button>
                </div>
              ) : (
                <Button variant="outline" className="w-full" onClick={startCamera}>
                  <Camera className="w-4 h-4 mr-2" /> Take Selfie (Optional)
                </Button>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </CardContent>
          </Card>
        )}

        {/* QR Code — Iter-291 — Camera scanner + manual fallback */}
        {!isCheckedIn && (
          <Card>
            <CardContent className="pt-4 space-y-3">
              {qrInput && (
                <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2" data-testid="qr-token-loaded">
                  <span>QR loaded: <span className="font-mono">{qrInput.slice(0, 16)}{qrInput.length > 16 ? '…' : ''}</span></span>
                  <button onClick={() => setQrInput('')} className="text-emerald-700 hover:text-emerald-900" data-testid="qr-token-clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={qrInput ? 'outline' : 'default'}
                  onClick={() => setShowQrScanner(true)}
                  data-testid="qr-scan-btn"
                >
                  <QrCode className="w-4 h-4 mr-2" /> Scan QR
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowQrInput((v) => !v)}
                  data-testid="qr-manual-toggle-btn"
                >
                  Enter code
                </Button>
              </div>
              {showQrInput && (
                <input
                  type="text"
                  value={qrInput}
                  onChange={(e) => setQrInput(e.target.value)}
                  placeholder="Paste check-in code"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="qr-manual-input"
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Iter-291 — Camera QR scanner modal */}
        <QRScannerModal
          open={showQrScanner}
          onClose={() => setShowQrScanner(false)}
          onScan={(text) => {
            const token = text.startsWith('ieventur-checkin:') ? text.slice(17) : text.trim();
            setQrInput(token);
            setShowQrScanner(false);
            toast.success('QR captured');
          }}
        />

        {/* GPS Warning */}
        {locationError && (
          <Alert variant="destructive">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{locationError}</AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-4">
          {!isCheckedIn ? (
            <>
              <Button
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                onClick={() => handleCheckIn()}
                disabled={checkingIn}
                data-testid="checkin-button"
              >
                {checkingIn ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <LogIn className="w-5 h-5 mr-2" />
                )}
                {checkingIn ? 'Checking In...' : 'Check In'}
              </Button>

              {/*
                Iter-411i / Iter-411t — GPS-failure fallback.
                Iter-411t P0 fix: the enum was missing `gps_fallback_review`
                so every prior fallback attempt returned 500 → users saw
                "Failed to check in" and reported the button as "disabled".
                Backend enum fixed via migration iter411t; button also
                promoted from muted-amber-outline to solid-cyan so it can
                never be perceptually mistaken for a disabled action.

                Any time we have a geolocation error code, the talent gets
                a secondary, clearly-labelled path that:
                  • posts `gps_fallback=true` + `selfie_failed=true` when
                    there is no captured selfie (so it survives even a
                    selfie-required shift)
                  • posts the diagnostic reason code
                  • routes the record to the admin review queue
                  • never triggers the late-check-in penalty
                This is the escape hatch that would have unblocked
                Samantha at 12:01 PM in Perry, GA.
              */}
              {locationErrorCode && !showEventCode && (
                <>
                  <Button
                    className="w-full h-14 text-base bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg"
                    onClick={() => handleCheckIn({ viaFallback: true })}
                    disabled={checkingIn}
                    data-testid="checkin-without-gps-btn"
                  >
                    {checkingIn ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 mr-2" />
                    )}
                    Check in without GPS (I&apos;m onsite)
                  </Button>
                  <p className="text-xs text-cyan-800 text-center px-2">
                    Pending Admin verification. Your attendance and Reliability Score are protected.
                  </p>
                  {/*
                    Iter-411k — Event-code entry surfaces as a peer
                    fallback so the talent can present a legitimate,
                    non-suspicious verification when both GPS and QR
                    are down. A successful event-code match counts as
                    a real onsite check-in.
                  */}
                  <Button
                    variant="outline"
                    className="w-full h-12 text-sm border-cyan-400 bg-cyan-50 hover:bg-cyan-100 text-cyan-800"
                    onClick={() => setShowEventCode(true)}
                    data-testid="open-event-code-btn"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Enter event code from onsite lead
                  </Button>
                </>
              )}

              {showEventCode && (
                <div className="rounded-xl border border-cyan-300 bg-cyan-50 p-4 space-y-3" data-testid="event-code-panel">
                  <div className="text-sm font-semibold text-cyan-900">Event code check-in</div>
                  <p className="text-xs text-cyan-800">
                    Ask your onsite lead, client contact, or agency for
                    the 6-character event code and enter it here. This
                    counts as a legitimate onsite verification.
                  </p>
                  <input
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    maxLength={6}
                    value={eventCode}
                    onChange={(e) => setEventCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                    placeholder="ABC123"
                    className="w-full h-12 text-center tracking-[0.4em] font-mono text-lg rounded-lg border border-cyan-300 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    data-testid="event-code-input"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 h-11"
                      onClick={() => { setShowEventCode(false); setEventCode(''); }}
                      data-testid="event-code-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 h-11 bg-cyan-600 hover:bg-cyan-700 text-white"
                      onClick={verifyEventCode}
                      disabled={verifyingCode || eventCode.trim().length < 4}
                      data-testid="event-code-submit-btn"
                    >
                      {verifyingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Verify &amp; check in
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : !isCheckedOut ? (
            <>
              {/* V412 — Client Lead Capture (GGF/Zapier). Prominent
                  green pill placed ABOVE the check-out so it's easy
                  to reach mid-shift. Only rendered when the client
                  has configured a lead-capture URL. */}
              {leadCapture?.enabled && (
                <Button
                  className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
                  onClick={() => navigate(`/talent/bookings/${bookingId}/leads`)}
                  data-testid="capture-lead-btn"
                >
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Capture Lead
                  {leadCapture.submission_count > 0 && (
                    <Badge className="ml-2 bg-white/20 text-white border-0" data-testid="lead-count-badge">
                      {leadCapture.submission_count}
                    </Badge>
                  )}
                </Button>
              )}
              <Button
                className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
                onClick={() => handleCheckOut()}
                disabled={checkingOut}
                data-testid="checkout-button"
              >
                {checkingOut ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <LogOut className="w-5 h-5 mr-2" />
                )}
                {checkingOut ? 'Checking Out...' : 'Check Out'}
              </Button>
              {locationErrorCode && (
                <Button
                  className="w-full h-14 text-base bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg"
                  onClick={() => handleCheckOut({ viaFallback: true })}
                  disabled={checkingOut}
                  data-testid="checkout-without-gps-btn"
                >
                  {checkingOut ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 mr-2" />
                  )}
                  Check out without GPS (I&apos;m onsite)
                </Button>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 mx-auto text-blue-500 mb-2" />
              <p className="text-lg font-semibold text-slate-700">Shift Complete!</p>
              <p className="text-sm text-gray-400">You&apos;ve successfully checked out.</p>
            </div>
          )}
        </div>

        {/* Check-in Result Info */}
        {checkinRecord && checkinRecord.gps_warning && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              {checkinRecord.gps_warning}
            </AlertDescription>
          </Alert>
        )}
      </main>
    </div>
  );
};

export default TalentCheckIn;
